import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { translations } from '../translations';
import { sendDeleteAccountRequest } from '../contexts/db.DeleteAccount';
import '../styles/pages/DeleteAccount.css';

function DeleteAccount({ language }) {
  const [step, setStep] = useState('initial'); // initial, confirm, thankYou, form, success
  const [deleteData, setDeleteData] = useState(true); // Yes by default
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const t = translations[language];

  const handleStart = () => {
    setStep('confirm');
  };

  const handleConfirm = () => {
    if (deleteData) {
      setStep('form');
    } else {
      setStep('thankYou');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError(t.deleteAccountAllFieldsRequired || 'All fields are required');
      return;
    }

    try {
      setLoading(true);
      await sendDeleteAccountRequest({ firstName, lastName, email }, language);
      setStep('success');
    } catch (err) {
      console.error('Error sending deletion request:', err);
      setError(t.deleteAccountError || 'Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="delete-account-page">
      {/* Logo */}
      <div className="delete-account-logo">
        <Link to="/" className="logo-text">DysOrto</Link>
      </div>

      {/* Initial Step */}
      {step === 'initial' && (
        <div className="delete-account-card">
          <div className="delete-account-icon warning">⚠️</div>
          <h1>{t.deleteAccountTitle || 'Delete Account'}</h1>
          <p className="delete-account-description">
            {t.deleteAccountWarning || 'Deleting your account will permanently remove all your data, including your profile, subscription history, and all associated information. This action cannot be undone.'}
          </p>
          <p className="delete-account-note">
            {t.deleteAccountNote || 'If you proceed, you will need to provide your information to verify your identity.'}
          </p>
          <button className="delete-account-btn primary" onClick={handleStart}>
            {t.deleteAccountStart || 'Start'}
          </button>
        </div>
      )}

      {/* Confirmation Step */}
      {step === 'confirm' && (
        <div className="delete-account-card">
          <h2>{t.deleteAccountConfirmTitle || 'Confirm Deletion'}</h2>
          <p className="delete-account-question">
            {t.deleteAccountConfirmQuestion || 'Do you want to delete all information related to your account?'}
          </p>
          
          <div className="delete-account-options">
            <label className={`option-card ${deleteData ? 'selected' : ''}`}>
              <input
                type="radio"
                name="deleteData"
                checked={deleteData}
                onChange={() => setDeleteData(true)}
              />
              <span className="option-label">{t.yes || 'Yes'}</span>
              <span className="option-description">
                {t.deleteAccountYesDescription || 'Delete all my data permanently'}
              </span>
            </label>
            
            <label className={`option-card ${!deleteData ? 'selected' : ''}`}>
              <input
                type="radio"
                name="deleteData"
                checked={!deleteData}
                onChange={() => setDeleteData(false)}
              />
              <span className="option-label">{t.no || 'No'}</span>
              <span className="option-description">
                {t.deleteAccountNoDescription || 'I changed my mind'}
              </span>
            </label>
          </div>

          <button className="delete-account-btn primary" onClick={handleConfirm}>
            {t.ok || 'OK'}
          </button>
        </div>
      )}

      {/* Thank You Step (User chose No) */}
      {step === 'thankYou' && (
        <div className="delete-account-card">
          <div className="delete-account-icon success">💙</div>
          <h2>{t.deleteAccountThankYouTitle || 'Thank You!'}</h2>
          <p className="delete-account-description">
            {t.deleteAccountThankYouMessage || 'Thank you for choosing to stay with us. Your data is safe and secure. We appreciate your trust in DysOrto!'}
          </p>
          <p className="close-window-message">
            {t.closeWindowMessage || 'You can now close this window.'}
          </p>
        </div>
      )}

      {/* Form Step (User chose Yes) */}
      {step === 'form' && (
        <div className="delete-account-card">
          <h2>{t.deleteAccountFormTitle || 'Verify Your Identity'}</h2>
          <p className="delete-account-description">
            {t.deleteAccountFormDescription || 'Please provide your information to process the deletion request.'}
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName">
                {t.firstName || 'First Name'} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t.enterFirstName || 'Enter your first name'}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                {t.lastName || 'Last Name'} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t.enterLastName || 'Enter your last name'}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                {t.email || 'Email'} <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.enterEmail || 'Enter your email'}
                required
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="delete-account-btn danger" disabled={loading}>
              {loading ? (t.sending || 'Sending...') : (t.send || 'Send')}
            </button>
          </form>
        </div>
      )}

      {/* Success Step */}
      {step === 'success' && (
        <div className="delete-account-card">
          <div className="delete-account-icon success">✓</div>
          <h2>{t.deleteAccountSuccessTitle || 'Request Sent'}</h2>
          <p className="delete-account-description">
            {t.deleteAccountSuccessMessage || 'Your account deletion request has been sent successfully. Our team will process your request and you will receive a confirmation email shortly.'}
          </p>
          <p className="close-window-message">
            {t.closeWindowMessage || 'You can now close this window.'}
          </p>
        </div>
      )}
    </div>
  );
}

export default DeleteAccount;
